import { useMemo, useState, type ReactElement } from 'react';
import { Search, Copy, Check, Globe, Shield, FileCheck, UserCheck, CircleDot, Mail, Hash, Clock, LogIn } from 'lucide-react';
import { useAuth } from '../shared/context/useAuth';
import { getInitials } from '../shared/utils/stringUtils';
import './ProfilePage.css';

type CategoryId = 'platform' | 'system' | 'ace' | 'user' | 'other';

interface RoleEntry {
  raw: string;
  display: string;
}

interface Category {
  id: CategoryId;
  label: string;
  icon: ReactElement;
  entries: RoleEntry[];
}

function categorize(roles: string[]): Category[] {
  const platform: RoleEntry[] = [];
  const system: RoleEntry[] = [];
  const ace: RoleEntry[] = [];
  const user: RoleEntry[] = [];
  const other: RoleEntry[] = [];

  const seen = new Set<string>();
  for (const raw of roles) {
    const key = raw.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    if (/^sysadmin$/i.test(raw)) {
      system.push({ raw, display: raw });
    } else if (/^acecan/i.test(raw)) {
      ace.push({ raw, display: raw.replace(/^acecan/i, '') });
    } else if (/^user(can|is)/i.test(raw)) {
      user.push({ raw, display: raw.replace(/^user(can|is)/i, '') });
    } else if (/^sourcelink$/i.test(raw) || /user$/i.test(raw)) {
      platform.push({ raw, display: raw });
    } else {
      other.push({ raw, display: raw });
    }
  }

  const sortByRaw = (a: RoleEntry, b: RoleEntry) => a.raw.localeCompare(b.raw);

  const all: Category[] = [
    { id: 'platform', label: 'Platform Access', icon: <Globe size={18} />, entries: platform.sort(sortByRaw) },
    { id: 'system', label: 'System', icon: <Shield size={18} />, entries: system.sort(sortByRaw) },
    { id: 'ace', label: 'ACE Permissions', icon: <FileCheck size={18} />, entries: ace.sort(sortByRaw) },
    { id: 'user', label: 'User Permissions', icon: <UserCheck size={18} />, entries: user.sort(sortByRaw) },
    { id: 'other', label: 'Other', icon: <CircleDot size={18} />, entries: other.sort(sortByRaw) },
  ];
  return all.filter((c) => c.entries.length > 0);
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [copiedRaw, setCopiedRaw] = useState<string | null>(null);

  const categories = useMemo(() => categorize(user?.roles ?? []), [user?.roles]);

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!query) return categories;
    return categories
      .map((c) => ({
        ...c,
        entries: c.entries.filter(
          (e) => e.raw.toLowerCase().includes(query) || e.display.toLowerCase().includes(query),
        ),
      }))
      .filter((c) => c.entries.length > 0);
  }, [categories, query]);

  const totalPermissions = categories.reduce((sum, c) => sum + c.entries.length, 0);

  const handleCopy = (raw: string) => {
    const clipboard = navigator.clipboard;
    if (!clipboard) return;
    clipboard.writeText(raw).then(
      () => {
        setCopiedRaw(raw);
        window.setTimeout(() => setCopiedRaw((prev) => (prev === raw ? null : prev)), 1200);
      },
      () => { /* ignore clipboard errors */ },
    );
  };

  if (!user) {
    return (
      <div className="profile-page">
        <p className="profile-empty">No user session.</p>
      </div>
    );
  }

  const displayName = user.displayName || user.email || 'User';
  const initials = getInitials(displayName);
  const issued = formatDate(user.issuedAtUtc);
  const expires = formatDate(user.expiresAtUtc);

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div className="profile-hero-avatar">{initials}</div>
        <div className="profile-hero-body">
          <h1 className="profile-hero-name">{displayName}</h1>
          <div className="profile-hero-meta">
            <span className="profile-hero-meta-item">
              <Mail size={14} />
              {user.email}
            </span>
            {user.userId > 0 && (
              <span className="profile-hero-meta-item">
                <Hash size={14} />
                User ID {user.userId}
              </span>
            )}
            {issued && (
              <span className="profile-hero-meta-item">
                <LogIn size={14} />
                Signed in {issued}
              </span>
            )}
            {expires && (
              <span className="profile-hero-meta-item">
                <Clock size={14} />
                Session expires {expires}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="profile-stats">
        <div className="profile-stat-card">
          <div className="profile-stat-value">{totalPermissions}</div>
          <div className="profile-stat-label">Total</div>
        </div>
        {categories.map((c) => (
          <div key={c.id} className={`profile-stat-card profile-stat-${c.id}`}>
            <div className="profile-stat-value">{c.entries.length}</div>
            <div className="profile-stat-label">{c.label}</div>
          </div>
        ))}
      </section>

      <div className="profile-search">
        <Search size={16} className="profile-search-icon" />
        <input
          type="text"
          className="profile-search-input"
          placeholder="Search permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="profile-search-clear"
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            Clear
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="profile-no-results">
          <Search size={32} />
          <p>No permissions match "{search}"</p>
        </div>
      ) : (
        <div className="profile-groups">
          {filtered.map((category) => (
            <section key={category.id} className={`profile-group profile-group-${category.id}`}>
              <header className="profile-group-header">
                <span className="profile-group-icon">{category.icon}</span>
                <h2 className="profile-group-title">{category.label}</h2>
                <span className="profile-group-count">{category.entries.length}</span>
              </header>
              <div className="profile-chip-grid">
                {category.entries.map((entry) => {
                  const copied = copiedRaw === entry.raw;
                  return (
                    <button
                      key={entry.raw}
                      type="button"
                      className={`profile-chip ${copied ? 'is-copied' : ''}`}
                      onClick={() => handleCopy(entry.raw)}
                      title={`Click to copy ${entry.raw}`}
                    >
                      <span className="profile-chip-label">{entry.display}</span>
                      <span className="profile-chip-icon" aria-hidden="true">
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
